import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createMerchantAccountSchema } from "../../../utils/validationSchemas";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getMerchantInviteByCode,
  merchantOnboard,
} from "../../../services/onboardingService";
import {
  emailVerification,
  verifySignupExistingPassword,
} from "../../../services/authService";
import MerchantAccountSetupForm from "./MerchantAccountSetupForm";
import LinkedAccountPasswordModal from "../../common/LinkedAccountPasswordModal";

const CreateAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const signupResume = location.state?.signupResume;
  const signupResumeRef = useRef(signupResume);
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const [hasExistingAccount, setHasExistingAccount] = useState(null);
  const [isCheckingAccount, setIsCheckingAccount] = useState(false);
  const [accountCheckError, setAccountCheckError] = useState("");
  const [invitationEmail, setInvitationEmail] = useState("");
  const [existingAccountRoles, setExistingAccountRoles] = useState([]);
  const [showLinkedAccountPasswordModal, setShowLinkedAccountPasswordModal] =
    useState(false);
  const [resumePassword, setResumePassword] = useState("");
  const { verificationCode } = useParams();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);

  useEffect(() => {
    if (!verificationCode) {
      navigate("/");
    }
  }, [verificationCode, navigate]);

  const checkExistingAccount = useCallback(async () => {
    if (!verificationCode) return;

    setIsCheckingAccount(true);
    setAccountCheckError("");
    try {
      const invitation = await dispatch(
        getMerchantInviteByCode(verificationCode),
      ).unwrap();
      const email = invitation?.data?.email;
      if (!email) {
        throw new Error("Invitation response did not include an email address.");
      }
      setInvitationEmail(email);
      const emailCheck = await dispatch(emailVerification({ email })).unwrap();
      setExistingAccountRoles(emailCheck?.roles || []);
      const hasExistingAccount =
        typeof invitation?.data?.hasExistingAccount === "boolean"
          ? invitation.data.hasExistingAccount
          : Boolean(emailCheck?.data);
      setHasExistingAccount(hasExistingAccount);
      const resumedPassword =
        signupResumeRef.current?.role === "merchant" &&
        signupResumeRef.current?.proof === verificationCode &&
        signupResumeRef.current?.email?.toLowerCase() === email.toLowerCase()
          ? signupResumeRef.current.password
          : "";
      if (hasExistingAccount && resumedPassword) {
        setResumePassword(resumedPassword);
      } else if (hasExistingAccount) {
        setShowLinkedAccountPasswordModal(true);
      }
    } catch (error) {
      setAccountCheckError(
        error?.message || "Unable to verify this invitation. Please try again.",
      );
    } finally {
      setIsCheckingAccount(false);
    }
  }, [dispatch, verificationCode]);

  useEffect(() => {
    checkExistingAccount();
  }, [checkExistingAccount]);

  const validationSchema = useMemo(
    () =>
      hasExistingAccount
        ? createMerchantAccountSchema.omit(["confirmPassword"])
        : createMerchantAccountSchema,
    [hasExistingAccount],
  );

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const merchantPromise = await dispatch(
          merchantOnboard({
            name: values?.firstName + " " + values?.lastName,
            password: values?.password,
            code: verificationCode,
          })
        ).unwrap();
        if (merchantPromise?.success === true) {
          // Fire GTM event for merchant onboarding success
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "merchant_form_completed",
            formType: "Merchant",
          });
          navigate("/merchant/account-created");
        }
      } catch (error) {
        console.error("Error during merchant onboarding:", error);
      }
    },
  });
  const setFieldValue = formik.setFieldValue;

  useEffect(() => {
    if (!resumePassword || !hasExistingAccount) return;
    setFieldValue("password", resumePassword);
    setResumePassword("");
    navigate(location.pathname, { replace: true, state: null });
  }, [resumePassword, hasExistingAccount, setFieldValue, navigate, location.pathname]);

  const handleLinkedAccountPassword = async (password) => {
    await verifySignupExistingPassword({
      email: invitationEmail,
      password,
      role: "merchant",
      proof: verificationCode,
    });
    setFieldValue("password", password);
    setShowLinkedAccountPasswordModal(false);
  };

  const handleForgotLinkedAccountPassword = () => {
    navigate("/forget-password", {
      state: {
        signupResume: {
          role: "merchant",
          email: invitationEmail,
          proof: verificationCode,
          returnPath: location.pathname,
        },
      },
    });
  };

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    // setFieldValue,
  } = formik;

  const scrollToFirstError = (formErrors) => {
    const priority = [
      'firstName',
      'lastName',
      'password',
      'confirmPassword'
    ];
    let targetField = priority.find((key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key));
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) return;
    const el = document.querySelector(`[data-field="${targetField}"]`) || document.querySelector(`[name="${targetField}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof el.focus === 'function') {
        try { el.focus(); } catch (_) { }
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(Object.keys(validationErrors).reduce((acc, k) => { acc[k] = true; return acc; }, {}), true);
      scrollToFirstError(validationErrors);
      return;
    }
    handleSubmit(e);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPassword = () => {
    setConfirmShowPassword(!showConfirmPassword);
  };

  const accountStatusContent = isCheckingAccount ? (
    <p className="mt-6 text-sm text-[#767676]">
      Checking whether this email already has an account...
    </p>
  ) : accountCheckError ? (
    <div className="mt-6 text-sm text-red-600">
      <p>{accountCheckError}</p>
      <button
        type="button"
        className="mt-2 text-[#00ADEF] underline"
        onClick={checkExistingAccount}
      >
        Try again
      </button>
    </div>
  ) : hasExistingAccount ? (
    <p className="mt-6 text-sm text-[#767676]">
      An account already uses this email. Enter its password to use the same
      password for your merchant account.
    </p>
  ) : null;

  return (
    <>
    <MerchantAccountSetupForm
      values={formik.values}
      errors={errors}
      touched={touched}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSubmit={handleFormSubmit}
      loading={loading}
      showPassword={showPassword}
      togglePassword={togglePassword}
      showConfirmPassword={showConfirmPassword}
      toggleConfirmPassword={toggleConfirmPassword}
      hideConfirmPassword={Boolean(hasExistingAccount)}
      passwordReadOnly={Boolean(hasExistingAccount)}
      statusContent={accountStatusContent}
      hideFields={isCheckingAccount || Boolean(accountCheckError)}
      showSubmitButton={!accountCheckError}
      submitDisabled={
        loading || isCheckingAccount || hasExistingAccount === null
      }
      submitLabel="Create Account"
    />
    {showLinkedAccountPasswordModal && invitationEmail && (
      <LinkedAccountPasswordModal
        email={invitationEmail}
        roles={existingAccountRoles}
        targetRole="merchant"
        onVerify={handleLinkedAccountPassword}
        onForgotPassword={handleForgotLinkedAccountPassword}
        onUseDifferentEmail={() =>
          navigate("/merchant/getting-started", { replace: true, state: null })
        }
      />
    )}
    </>
  );
};

export default CreateAccount;
